import React, { useState, useEffect } from "react";
import { BaseDialog } from "../../shared/BaseDialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { Button } from "@shared/ui/button";
import { FormService, FormField } from "@shared/services/FormServices";
import { Info } from "lucide-react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

interface SplitTransformationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  targetField: string;
  sourceColumn?: string;
  fileData: string[];
  availableTargetFields: string[];
  onApplyTransformation: (config: any) => void;
}

const SplitTransformationDialog: React.FC<SplitTransformationDialogProps> = ({
  isOpen,
  onClose,
  targetField,
  fileData,
  availableTargetFields,
  onApplyTransformation,
}) => {
  const [formData, setFormData] = useState({
    delimiter: "",
    targetFields: [] as string[],
  });

  const [previewData, setPreviewData] = useState<{
    before: string[];
    after: string[][];
  }>({
    before: [],
    after: [],
  });

  useEffect(() => {
    if (fileData) {
      const samples = fileData
        .map((s) => s.trim())
        .filter((s) => s && s !== "...")
        .slice(0, 2);
      setPreviewData({ before: samples, after: samples.map((s) => [s]) });
    }
  }, [fileData]);

  const fields: FormField[] = [
    {
      name: "delimiter",
      type: "text",
      label: "Delimiter",
      placeholder: "e.g., , (comma) or | (pipe) or space",
      helperText:
        "Enter the character(s) that separate the data you want to split",
      className: "col-span-1",
    },
    {
      name: "targetFields",
      type: "multi-select",
      label: "Split into Target Fields",
      options: availableTargetFields.map((field) => ({
        value: field,
        label: field,
      })),
      helperText: "Choose which fields will receive the split parts (in order)",
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields,
    mode: "onChange",
    defaultValues: formData,
  });

  const handleFieldChange = (fieldName: string, value: any) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);

    updatePreview(newFormData.delimiter, newFormData.targetFields);
  };

  const updatePreview = (delimiter: string, targetFields: string[]) => {
    if (!delimiter || previewData.before.length === 0) return;

    const actualDelimiter =
      delimiter.toLowerCase() === "space" || delimiter === " "
        ? " "
        : delimiter === "\\t" || delimiter.toLowerCase() === "tab"
        ? "\t"
        : delimiter;

    const splitResults = previewData.before.map((sample) => {
      const parts = sample.split(actualDelimiter).map((p) => p.trim());
      return targetFields.length > 0
        ? parts.slice(0, targetFields.length)
        : parts;
    });

    setPreviewData((prev) => ({ ...prev, after: splitResults }));
  };

  const handleApply = () => {
    if (!formData.delimiter || formData.targetFields.length === 0) return;

    onApplyTransformation({
      targetField: toSnakeCase(targetField),
      transformation: {
        type: "split",
        order: 1,
        config: {
          delimiter: formData.delimiter,
          targetFields: formData.targetFields.map(toSnakeCase),
        },
      },
    });
    onClose();
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Split Configuration"
      className="max-w-4xl w-full max-h-[90vh] flex flex-col"
    >
      {/* Body */}
      <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
        <div className="flex items-start gap-3 text-sm text-gray-600 bg-blue-50 p-4 rounded-md">
          <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Split Data:</strong> Use this to separate full names into
            first/last name, or addresses into components. Common delimiters:
            comma (,), semicolon (;), pipe (|), space.
          </div>
        </div>

        {/* Form */}
        <DynamicForm
          fields={fields}
          form={form}
          config={{
            fields,
            layout: "vertical",
            showSubmitButton: false,
            showResetButton: false,
            classes: FIELD_CLASSES,
          }}
          onFieldChange={handleFieldChange}
          gridColumns={1}
        />

        {/* Live Preview */}
        {previewData.before.length > 0 && (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium mb-3 text-gray-900">Preview</h4>
              <div className="grid grid-cols-2 gap-6 relative">
                {/* Before */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    Before
                  </p>
                  <div className="space-y-2">
                    {previewData.before.map((item, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-3 rounded-md border border-gray-200"
                      >
                        <span className="text-sm text-gray-800">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Arrow */}
                <div className="flex items-center justify-center absolute left-1/2 transform -translate-x-1/2">
                  <span className="text-gray-400">→</span>
                </div>

                {/* After */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-3">
                    After
                  </p>
                  <div className="space-y-2">
                    {previewData.after.map((splitArray, idx) => (
                      <div
                        key={idx}
                        className="bg-white p-3 rounded-md border border-gray-200"
                      >
                        {splitArray.length > 0 ? (
                          formData.targetFields.map((field, fieldIdx) => (
                            <div key={fieldIdx} className="text-sm">
                              <span className="text-gray-500">{field}:</span>{" "}
                              <span className="text-gray-800 font-medium">
                                {splitArray[fieldIdx] || "(empty)"}
                              </span>
                            </div>
                          ))
                        ) : (
                          <span className="text-sm text-gray-400">
                            Enter delimiter to see preview
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!formData.delimiter || formData.targetFields.length === 0}
          >
            Apply Transformation
          </Button>
        </div>
      </div>
    </BaseDialog>
  );
};

export default SplitTransformationDialog;
